import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Pagination,
  InputAdornment,
  Tooltip,
  Alert,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';

import Navbar from '@/components/home/Navbar';

import knowledgeBaseService, { KnowledgeBase } from '@/services/knowledgeBaseService';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return theme.palette.grey[500];
      case 'building':
        return theme.palette.warning.main;
      case 'completed':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getTextColor = (status: string) => {
    switch (status) {
      case 'new':
        return theme.palette.getContrastText(theme.palette.grey[500]);
      case 'building':
        return theme.palette.getContrastText(theme.palette.warning.main);
      case 'completed':
        return theme.palette.getContrastText(theme.palette.success.main);
      default:
        return theme.palette.getContrastText(theme.palette.grey[500]);
    }
  };

  return {
    backgroundColor: getStatusColor(status),
    color: getTextColor(status),
    fontWeight: 'bold',
  };
});

interface CreateKnowledgeBaseDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateKnowledgeBaseDialog: React.FC<CreateKnowledgeBaseDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_enabled: true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await knowledgeBaseService.createKnowledgeBase(formData);
      onSuccess();
      onClose();
      setFormData({ name: '', description: '', is_enabled: true });
    } catch (error: any) {
      console.error('创建知识库失败:', error);
      // 这里可以添加错误提示
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>创建知识库</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="知识库名称"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="知识库描述"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="描述该知识库的用途和内容，这将帮助AI系统选择合适的知识库进行查询"
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_enabled}
                onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
              />
            }
            label="启用知识库"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            取消
          </Button>
          <Button type="submit" variant="contained" disabled={loading || !formData.name}>
            {loading ? '创建中...' : '创建'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface EditKnowledgeBaseDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  knowledgeBase: KnowledgeBase | null;
}

const EditKnowledgeBaseDialog: React.FC<EditKnowledgeBaseDialogProps> = ({
  open,
  onClose,
  onSuccess,
  knowledgeBase,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_enabled: true,
  });
  const [loading, setLoading] = useState(false);

  // 当知识库数据变化时，更新表单数据
  useEffect(() => {
    if (knowledgeBase) {
      setFormData({
        name: knowledgeBase.name,
        description: knowledgeBase.description || '',
        is_enabled: knowledgeBase.is_enabled,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        is_enabled: true,
      });
    }
  }, [knowledgeBase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!knowledgeBase) return;

    setLoading(true);

    try {
      await knowledgeBaseService.updateKnowledgeBase(knowledgeBase.id, formData);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('更新知识库失败:', error);
      // 这里可以添加错误提示
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>编辑知识库</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="知识库名称"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="知识库描述"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="描述该知识库的用途和内容，这将帮助AI系统选择合适的知识库进行查询"
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_enabled}
                onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
              />
            }
            label="启用知识库"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            取消
          </Button>
          <Button type="submit" variant="contained" disabled={loading || !formData.name}>
            {loading ? '更新中...' : '更新'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const KnowledgeBasePage: React.FC = () => {
  const navigate = useNavigate();
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const pageSize = 20;

  const fetchKnowledgeBases = async () => {
    setLoading(true);
    try {
      const response = await knowledgeBaseService.getKnowledgeBases({
        page,
        page_size: pageSize,
        search: searchTerm || undefined,
      });
      setKnowledgeBases(response.data);
      setTotalPages(response.total_pages);
      setTotal(response.total);
    } catch (error: any) {
      console.error('获取知识库列表失败:', error);
      setSnackbar({ open: true, message: '获取知识库列表失败', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledgeBases();
  }, [page, searchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchKnowledgeBases();
  };

  const handleDelete = async (knowledgeBase: KnowledgeBase) => {
    try {
      await knowledgeBaseService.deleteKnowledgeBase(knowledgeBase.id);
      setSnackbar({ open: true, message: '知识库删除成功', severity: 'success' });
      fetchKnowledgeBases();
    } catch (error: any) {
      console.error('删除知识库失败:', error);
      setSnackbar({ open: true, message: '删除知识库失败', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedKnowledgeBase(null);
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return '新建';
      case 'building':
        return '构建中';
      case 'completed':
        return '完成';
      default:
        return status;
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  return (
    <>
      <Navbar />
      <Box p={3}>
        {/* 页面标题和操作 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          公共知识库
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchKnowledgeBases}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            刷新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            创建知识库
          </Button>
        </Box>
      </Box>

      {/* 搜索栏 */}
      <Box component="form" onSubmit={handleSearch} mb={3}>
        <TextField
          fullWidth
          placeholder="搜索知识库名称或描述..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button type="submit" variant="outlined" size="small">
                  搜索
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* 统计信息 */}
      <Box mb={3}>
        <Typography variant="body2" color="text.secondary">
          共找到 {total} 个知识库
        </Typography>
      </Box>

      {/* 加载进度 */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* 知识库列表 */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>名称</TableCell>
              <TableCell>描述</TableCell>
              <TableCell>数据集名称</TableCell>
              <TableCell>状态</TableCell>
              <TableCell>文档数量</TableCell>
              <TableCell>启用状态</TableCell>
              <TableCell>创建时间</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {knowledgeBases.map((kb) => (
              <StyledTableRow key={kb.id}>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {kb.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                    {kb.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {kb.dataset_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusChip
                    label={getStatusText(kb.build_status)}
                    status={kb.build_status}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {kb.document_count}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={kb.is_enabled ? '启用' : '未启用'}
                    color={kb.is_enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTime(kb.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Tooltip title="查看详情">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/knowledge-base/${kb.id}`)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="编辑">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedKnowledgeBase(kb);
                          setEditDialogOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="删除">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedKnowledgeBase(kb);
                          setDeleteDialogOpen(true);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分页 */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* 创建知识库对话框 */}
      <CreateKnowledgeBaseDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={fetchKnowledgeBases}
      />

      {/* 编辑知识库对话框 */}
      <EditKnowledgeBaseDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={() => {
          fetchKnowledgeBases();
          setSnackbar({ open: true, message: '知识库更新成功', severity: 'success' });
        }}
        knowledgeBase={selectedKnowledgeBase}
      />

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除知识库 "{selectedKnowledgeBase?.name}" 吗？此操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button
            onClick={() => selectedKnowledgeBase && handleDelete(selectedKnowledgeBase)}
            color="error"
            variant="contained"
          >
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </>
  );
};

export default KnowledgeBasePage;